import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Permissions } from '@app/core/guard/Permissions';
import { Roles } from '@app/core/guard/Roles';
import { VaultBaseComponent } from '@app/shared/components/base/vault-base.component';
import { TableAction, TableActions } from '@app/shared/components/table/table.component';
import { AppState } from '@app/state';
import { selectIsMobile } from '@app/state/settings/settings.selectors';
import { FinancialInstitution } from '@interfaces/a-organizar/financialInstitution';
import { Pagination, TablePagination } from '@interfaces/common/pagination';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from '@services/helpers/confirmation.service';
import { PermissionsService } from '@services/helpers/permissions.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { UtilsService } from '@services/helpers/utils.service';
import { FinancialInstitutionService } from '@services/http/financial-institution.service';
import { ColumnDefinition } from '@src/app/shared/interfaces/column-definition.interface';
import { MenuItem } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';
import { Observable } from 'rxjs';
import { ModalBankManagersComponent } from './components/modal-bank-managers/modal-bank-managers.component';

@Component({
  templateUrl: './financial-institution-list.component.html',
  styleUrls: ['./financial-institution-list.component.scss'],
  standalone: false, // eslint-disable-line
})
export class FinancialInstitutionListComponent extends VaultBaseComponent implements OnInit {
  isMobile: boolean = false;
  isAdmin = false;
  breadcrumbs: MenuItem[] = [];
  home: MenuItem = {
    icon: 'pi pi-home',
    routerLink: '/dashboard/dashboard-management',
    label: this.t(this.tk.General.Dashboard),
  };

  financialInstitutions: FinancialInstitution[] = [];
  paginationRequest: TablePagination = {
    Page: 0,
    PageSize: 10,
  };
  pagination: Pagination = {
    total: 0,
    currentPage: 0,
    pages: 0,
    itemsPerPage: 0,
  };
  search: string = '';
  financialInstitutionPermissions = Permissions.FinancialInstitution;
  columnDefinitions: ColumnDefinition[] = [];
  selectedFinancialInstitutionId: string | null = null;
  selectedFinancialInstitutionName: string = '';
  /** Listagem em andamento: começa verdadeiro para a tabela abrir no esqueleto, não em "Nenhum item". */
  loading = true;
  /** A primeira listagem já chegou: daí em diante o recarregamento usa o loading global. */
  private financialInstitutionsLoadedOnce = false;

  @ViewChild('bankManagersModal') bankManagersModal!: ModalBankManagersComponent;

  constructor(
    translateService: TranslateService,
    private utilsService: UtilsService,
    private router: Router,
    private toast: ToastHelperService,
    private financialInstitutionService: FinancialInstitutionService,
    private confirmationService: ConfirmationService,
    private permissionsService: PermissionsService,
    store: Store<AppState>
  ) {
    super(translateService, store);
  }

  ngOnInit(): void {
    this.verifyIsMobile();

    // Verificar se o usuário é ADMIN
    this.permissionsService.hasRole(Roles.ADMIN).subscribe(isAdmin => {
      this.isAdmin = isAdmin;
    });

    this.breadcrumbs = [
      {
        label: this.t(this.tk.Management.Management),
        routerLink: '/',
      },
      {
        label: this.t(this.tk.Management.FinancialInstitution),
        routerLink: '/dashboard/financial-institution/list',
      },
    ];

    this.columnDefinitions = [
      new ColumnDefinition({ field: 'logo', header: 'Logo' }),
      new ColumnDefinition({ field: 'sequentialId', header: 'Id' }),
      new ColumnDefinition({ field: 'institutionName', header: 'Nome' }),
      new ColumnDefinition({ field: 'compensationCode', header: 'Código de Compensação' }),
      new ColumnDefinition({ field: 'ispb', header: 'ISPB' }),
      new ColumnDefinition({ field: 'shortName', header: 'Nome Curto' }),
      new ColumnDefinition({ field: 'displayName', header: 'Nome de Exibição' }),
      new ColumnDefinition({ field: 'network', header: 'Rede' }),
      new ColumnDefinition({ field: 'url', header: 'URL' }),
      new ColumnDefinition({ field: 'cnpj', header: 'CNPJ' }),
      new ColumnDefinition({ field: 'options', header: '' }),
    ];

    this.getFinancialInstitutions();
  }

  getFinancialInstitutions() {
    // Primeira carga: a tabela mostra o esqueleto, sem o loading global por cima.
    // Recarregamentos (busca, página, exclusão) seguem com o loading global.
    const showGlobalLoading = this.financialInstitutionsLoadedOnce;
    if (showGlobalLoading) this.utilsService.setIsLoading(true);
    this.loading = true;
    this.financialInstitutionService
      .getFinancialInstitutions(
        {
          ...this.paginationRequest,
          Page: (this.paginationRequest?.Page || 0) + 1,
        },
        this.search
      )
      .subscribe({
        next: response => {
          if (showGlobalLoading) this.utilsService.setIsLoading(false);
          this.financialInstitutionsLoadedOnce = true;
          this.loading = false;
          if (response) {
            this.financialInstitutions = response.data;
            const { total, currentPage, pages, itemsPerPage } = response;
            this.pagination = {
              total: total as number,
              currentPage: currentPage as number,
              pages,
              itemsPerPage: itemsPerPage as number,
            };
          }
        },
        error: error => {
          if (showGlobalLoading) this.utilsService.setIsLoading(false);
          this.financialInstitutionsLoadedOnce = true;
          this.loading = false;
          console.error(error);
          this.toast.showToastError(
            this.t(this.tk.Management.ErrorLoadingFinancialInstitutions),
            error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
          );
        },
      });
  }

  handleTablePagination(pagination: PaginatorState) {
    this.paginationRequest = {
      Page: pagination.page,
      PageSize: pagination.rows,
    };
    this.getFinancialInstitutions();
  }

  addFinancialInstitution(): void {
    this.router.navigate(['dashboard/financial-institution/register']);
  }

  handleTableAction(
    tableAction: TableAction<FinancialInstitution> | TableActions<FinancialInstitution>
  ): void {
    // Normalizar para TableActions
    const tableActions: TableActions<FinancialInstitution> =
      'items' in tableAction
        ? tableAction
        : { action: tableAction.action, items: [tableAction.item] };

    switch (tableActions.action) {
      case 'delete': {
        const item = tableActions.items[0];
        this.confirmationService
          .confirm(
            this.t(this.tk.Management.FinancialInstitutionTitleDeleteConfirmation),
            this.t(this.tk.Management.FinancialInstitutionDescriptionDeleteConfirmation)
          )
          .subscribe(confirmed => {
            if (confirmed) {
              this.utilsService.setIsLoading(true);
              this.removeFinancialInstitution(item).subscribe({
                next: () => {
                  this.utilsService.setIsLoading(false);
                  this.toast.showToastSuccess(
                    this.t(this.tk.Management.FinancialInstitutionDeletedSuccessfully),
                    ''
                  );
                  this.financialInstitutions = [];
                  this.getFinancialInstitutions();
                },
                error: error => {
                  this.utilsService.setIsLoading(false);
                  console.log('removeFinancialInstitution error: ', error);

                  this.toast.showToastError(
                    this.t(this.tk.Management.ErrorRemovingFinancialInstitution),
                    error?.errors?.length
                      ? error.errors[0].message
                      : this.t(this.tk.General.RequestError)
                  );
                },
              });
            }
          });
        break;
      }
      default:
        break;
    }
  }

  removeFinancialInstitution(
    financialInstitution: FinancialInstitution
  ): Observable<FinancialInstitution | null> {
    return this.financialInstitutionService.deleteFinancialInstitution(financialInstitution.id);
  }

  handleSearch(search: string = ''): void {
    this.search = search;
    this.getFinancialInstitutions();
  }

  verifyIsMobile() {
    if (!this.store) return;
    this.store.select(selectIsMobile).subscribe(isMobile => (this.isMobile = isMobile));
  }

  getMenuActions(item: FinancialInstitution): MenuItem[] {
    const actions: MenuItem[] = [
      {
        label: this.t(this.tk.General.Update),
        icon: 'pi pi-pencil',
        command: () => {
          this.router.navigate(['dashboard/financial-institution/register', item.id]);
        },
      },
      {
        label: 'Gerentes',
        icon: 'pi pi-users',
        command: () => {
          this.openBankManagersModal(item);
        },
      },
    ];

    // Adicionar a ação apenas para ADMIN
    if (this.isAdmin) {
      actions.push({
        label: 'Gerenciar Origens de Funding',
        icon: 'pi pi-globe',
        command: () => {
          this.router.navigate([
            'dashboard/financial-institution',
            item.id,
            'loan-operation-origins',
          ]);
        },
      });
    }

    actions.push({
      label: this.t(this.tk.General.Delete),
      icon: 'pi pi-trash',
      command: () => {
        this.handleTableAction({ action: 'delete', items: [item] });
      },
    });

    return actions;
  }

  openBankManagersModal(institution: FinancialInstitution): void {
    this.selectedFinancialInstitutionId = institution.id;
    this.selectedFinancialInstitutionName =
      institution.institutionName || institution.displayName || '';
    if (this.bankManagersModal) {
      this.bankManagersModal.open();
    }
  }

  onBankManagersModalClosed(): void {
    this.selectedFinancialInstitutionId = null;
    this.selectedFinancialInstitutionName = '';
  }
}
